/* eslint-disable consistent-return */
/* eslint-disable no-param-reassign */
import { useEffect } from 'react';
import { useQuery } from 'react-query';
import produce from 'immer';
import { useToast } from '@kcd/design-system';

import {
  useOAIInvalidateQueries,
  useOAIMutation,
  useOAIOptimisticMutation,
  useOAIQuery,
} from '~/hooks/reactQuery';
import type { CART_TYPE } from '~/hooks/useCartTypeParam';
import { fetchOAIData } from '~/libs/openapi';
import type { OAISchema } from '~/types/openapi';

import { flatVendorGroupsShoppingCartItem } from './utils';
import { getSnowdonJwt } from '../auth/utils';

/**
 * 장바구니에 담긴 상품 갯수 조회
 * */
export const useShoppingCartCount = () => {
  const query = useOAIQuery({
    queryKey: '/api/market-place/v1/shopping-cart-items/count',
    queryOptions: {
      suspense: true,
    },
  });

  return {
    count: query.data || 0,
    ...query,
  };
};

/**
 * 상품 유효성 검사
 * */
export const useValidateShoppingCart = () => {
  return useOAIMutation({
    method: 'post',
    mutationKey: '/api/market-place/v1/shopping-cart-items/validate',
  });
};

/**
 * 구매 불가능한 상품의 체크 제거
 * */
export const useDeselectNotNormalSelectedCartItems = () => {
  return useOAIMutation({
    method: 'patch',
    mutationKey:
      '/api/market-place/v1/shopping-cart-items/not-normal/selection/disable',
  });
};

/**
 * 선택 입점사 상품들의 선택 or 해제
 * */
export const useToggleVendorGroupAllItemsSelect = () => {
  const mutate = useOAIOptimisticMutation({
    method: 'patch',
    mutationKey: '/api/market-place/v1/shopping-cart-items/all-selection',
    updateQueryKey: '/api/market-place/v3/shopping-cart-items',
    invalidateQueryKeys: [
      '/api/market-place/v3/shopping-cart-items',
      '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment',
      '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment/nudge',
    ],
    updater: ({ old, variables: { requestBody } }) => {
      return produce(old, (current) => {
        const targetGroups = (() => {
          if (requestBody.vendorId) {
            return current.shoppingCartVendorGroups.filter(
              ({ vendorId }) => vendorId === requestBody.vendorId,
            );
          }

          return current.shoppingCartVendorGroups;
        })();

        targetGroups.forEach((group) => {
          group.shoppingCartItemBundles.forEach((bundle) => {
            bundle.shoppingCartItems.forEach((cartItem) => {
              // eslint-disable-next-line no-param-reassign
              cartItem.isSelected = requestBody.isSelected;
            });
          });
        });
      });
    },
  });

  return mutate;
};

/**
 * 상품의 선택 Check를 바꿈
 * */

export const useToggleCartItemSelect = (vendorId: number) => {
  const mutate = useOAIOptimisticMutation({
    method: 'patch',
    mutationKey: '/api/market-place/v1/shopping-cart-items/selection',
    updateQueryKey: '/api/market-place/v3/shopping-cart-items',
    invalidateQueryKeys: [
      '/api/market-place/v3/shopping-cart-items',
      '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment',
      '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment/nudge',
    ],
    updater: ({ old, variables: { requestBody } }) => {
      return produce(old, (current) => {
        const target = current.shoppingCartVendorGroups.find(
          (group) => group.vendorId === vendorId,
        );
        if (!target) return old;

        target.shoppingCartItemBundles.forEach((bundle) => {
          bundle.shoppingCartItems.forEach((cartItem) => {
            if (
              cartItem.shoppingCartItemId === requestBody.shoppingCartItemId
            ) {
              cartItem.isSelected = requestBody.isSelected;
            }
          });
        });
      });
    },
  });

  return mutate;
};

/**
 * 장바구니 상품 조회
 * */
export const useShoppingCartItemsV3 = (paymentType: CART_TYPE) => {
  const toast = useToast();
  const invalidateQueries = useOAIInvalidateQueries();

  const { mutateAsync: deselectNotNormalItems } =
    useDeselectNotNormalSelectedCartItems();
  const { mutateAsync: confirmChanges } = useOAIMutation({
    method: 'put',
    mutationKey: `/api/market-place/v1/shopping-cart-items/confirm-changes`,
  });

  const { data, ...query } = useQuery(
    '/api/market-place/v3/shopping-cart-items',
    async ({ signal }) => {
      await deselectNotNormalItems({
        cartType: paymentType,
      });
      const result = await fetchOAIData<
        OAISchema['MarketShoppingCartResponseV3']
      >({
        queryKey: '/api/market-place/v3/shopping-cart-items',
        variables: {
          cartType: paymentType,
        },
        fetchOptions: {
          signal,
          headers: {
            'X-Client-Auth-Token': getSnowdonJwt(),
          },
        },
      });

      return result;
    },
    { cacheTime: 0, keepPreviousData: true, suspense: true },
  );

  useEffect(() => {
    if (data?.shouldNotify) {
      toast('상품 중 정보가 변동된 상품이 있어\n최신 정보를 가져왔습니다.');
      const { changedCartItemIds = [] } = data;
      void confirmChanges({
        requestBody: {
          shoppingCartItemIds: changedCartItemIds,
        },
      }).then(() => {
        void invalidateQueries(
          '/api/market-place/v3/shopping-cart-items',
          '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment',
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.shouldNotify]);

  const flatList = flatVendorGroupsShoppingCartItem(
    data?.shoppingCartVendorGroups || [],
  );

  return {
    data,
    flatList,
    totalCount:
      (data?.unbuyableShoppingCartItems?.length || 0) + (flatList?.length || 0),
    ...query,
  };
};

export const useShoppingCartPrice = (paymentType: CART_TYPE) => {
  return useOAIQuery({
    queryKey:
      '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment',
    queryOptions: {
      suspense: true,
      keepPreviousData: true,
    },
    variables: {
      cartType: paymentType,
    },
  });
};

/**
 * 장바구니 페이지에서 선택상품들 제거
 * */
export const useDeleteShoppingCartItems = () => {
  const { mutateAsync } = useOAIMutation({
    method: 'delete',
    mutationKey: '/api/market-place/v1/shopping-cart-items',
  });

  return mutateAsync;
};

/**
 * 상품 단건 삭제
 * */

export const useDeleteShoppingCartItemByShoppingCartItemId = (
  vendorId: number,
) => {
  const mutate = useOAIOptimisticMutation({
    method: 'delete',
    mutationKey:
      '/api/market-place/v1/shopping-cart-items/{shoppingCartItemId}',
    updateQueryKey: '/api/market-place/v3/shopping-cart-items',
    invalidateQueryKeys: [
      '/api/market-place/v3/shopping-cart-items',
      '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment',
      '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment/nudge',
    ],
    updater: ({ old, variables: { shoppingCartItemId } }) => {
      return produce(old, (current) => {
        const targetVendorIndex = current.shoppingCartVendorGroups.findIndex(
          (group) => group.vendorId === vendorId,
        );
        if (targetVendorIndex === -1) return;

        current.shoppingCartVendorGroups[
          targetVendorIndex
        ].shoppingCartItemBundles.forEach((bundle) => {
          const targetIndex = bundle.shoppingCartItems.findIndex(
            (item) => item.shoppingCartItemId === shoppingCartItemId,
          );

          if (targetIndex !== -1) {
            bundle.shoppingCartItems.splice(targetIndex, 1);
          }
        });

        const hasVendorNoItems = (() => {
          return current.shoppingCartVendorGroups[
            targetVendorIndex
          ].shoppingCartItemBundles.every(
            (bundle) => !bundle.shoppingCartItems.length,
          );
        })();

        if (hasVendorNoItems) {
          current.shoppingCartVendorGroups.splice(targetVendorIndex, 1);
        }
      });
    },
  });

  return mutate;
};

export const useDeleteUnbuyableShoppingCartItem = () => {
  const mutate = useOAIOptimisticMutation({
    method: 'delete',
    mutationKey:
      '/api/market-place/v1/shopping-cart-items/{shoppingCartItemId}',
    updateQueryKey: '/api/market-place/v3/shopping-cart-items',
    invalidateQueryKeys: [
      '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment',
    ],
    updater: ({ old, variables: { shoppingCartItemId } }) => {
      return produce(old, (current) => {
        const targetIndex = current.unbuyableShoppingCartItems.findIndex(
          (cartItem) => cartItem.shoppingCartItemId === shoppingCartItemId,
        );

        if (targetIndex === -1) return;

        current.unbuyableShoppingCartItems.splice(targetIndex, 1);
      });
    },
  });

  return mutate;
};

/**
 * 장바구니 특정 상품의 수량 변경
 * */
export const useUpdateCartItemFixQuantity = (vendorId: number) => {
  const mutate = useOAIOptimisticMutation({
    method: 'put',
    mutationKey: '/api/market-place/v2/shopping-cart-items/fix-quantity',
    updateQueryKey: '/api/market-place/v3/shopping-cart-items',
    invalidateQueryKeys: [
      '/api/market-place/v3/shopping-cart-items',
      '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment',
      '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment/nudge',
    ],
    updater: ({ old, variables: { requestBody } }) => {
      return produce(old, (current) => {
        const targetGroups = current.shoppingCartVendorGroups.find(
          (group) => group.vendorId === vendorId,
        );
        if (targetGroups) {
          targetGroups.shoppingCartItemBundles.forEach((bundle) => {
            bundle.shoppingCartItems.forEach((item) => {
              if (item.skuId === requestBody.stockKeepingUnits[0].skuId) {
                item.quantity = requestBody.stockKeepingUnits[0].quantity;
              }
            });
          });
        }
      });
    },
  });

  return mutate;
};

/**
 *  옵션의 수량 추가
 * */
export const useUpdateCartQauntity = () => {
  const { mutateAsync: updateShoppingCartQuantity } = useOAIMutation({
    method: 'put',
    mutationKey: '/api/market-place/v2/shopping-cart-items/quantity',
  });

  return updateShoppingCartQuantity;
};

export const useUpdateQuickQauntity = () => {
  const { mutateAsync: updateQuickQuantity } = useOAIMutation({
    method: 'put',
    mutationKey: '/api/market-place/v2/shopping-cart-items/quick/quantity',
  });

  return updateQuickQuantity;
};

export const useUpdateECoponQuick = () => {
  const { mutateAsync: updateQuickQuantity } = useOAIMutation({
    method: 'put',
    mutationKey: '/api/market-place/v2/shopping-cart-items/quick/e-coupon',
  });

  return updateQuickQuantity;
};
